
import { base44 } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ClipboardList, AlertTriangle, AlertCircle, Car } from "lucide-react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function EAStaffDashboard() {
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me(),
  });

  // Check if user has EA staff access
  const isEAStaff = user?.role === 'admin' || (user?.role === 'user' && user?.user_type === 'ea_staff');

  if (!isEAStaff) {
    return (
      <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            You do not have permission to access this page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const { data: rosterEntries = [] } = useQuery({
    queryKey: ['rosterEntries'],
    queryFn: () => base44.entities.RosterEntry.list(),
  });

  const { data: trackingRecords = [] } = useQuery({
    queryKey: ['eaMissTracking'],
    queryFn: () => base44.entities.EAMissTracking.list(),
  });

  // Count unique students (not assignments)
  const activeStudents = new Set(rosterEntries.filter(e => e.status === 'active' || e.status === 'excused').map(e => e.other_id));
  const refusalStudents = new Set(rosterEntries.filter(e => e.status === 'asd').map(e => e.other_id));
  const activeConsequences = trackingRecords.filter(r => r.status === 'active').length;
  
  const activeCount = activeStudents.size;
  const refusalCount = refusalStudents.size;

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">EA Staff Dashboard</h1>
          <p className="text-slate-600 mt-1">Welcome, {user?.full_name || 'EA Staff'}</p>
        </div>

        <Alert className="bg-blue-50 border-blue-200">
          <AlertCircle className="w-4 h-4 text-blue-600" />
          <AlertDescription className="text-blue-900">
            <strong>Read-Only Access:</strong> You can view and search all rosters to help students with questions. Attendance processing is managed by administrators.
          </AlertDescription>
        </Alert>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-none shadow-lg bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">EA Roster</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-4xl font-bold">{activeCount}</div>
                <ClipboardList className="w-12 h-12 opacity-80" />
              </div>
              <p className="text-xs mt-2 opacity-75">Active students</p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-red-500 to-red-600 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">ASD Roster</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-4xl font-bold">{refusalCount}</div>
                <AlertTriangle className="w-12 h-12 opacity-80" />
              </div>
              <p className="text-xs mt-2 opacity-75">Students in ASD</p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">Active Consequences</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-4xl font-bold">{activeConsequences}</div>
                <AlertTriangle className="w-12 h-12 opacity-80" />
              </div>
              <p className="text-xs mt-2 opacity-75">Needs intervention</p>
            </CardContent>
          </Card>
        </div>

        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to={createPageUrl("RunningRoster")}>
              <Button className="w-full justify-start bg-blue-600 hover:bg-blue-700" size="lg">
                <ClipboardList className="w-5 h-5 mr-3" />
                View EA Roster
              </Button>
            </Link>
            <Link to={createPageUrl("ASDRoster")}>
              <Button className="w-full justify-start bg-red-600 hover:bg-red-700" size="lg">
                <AlertTriangle className="w-5 h-5 mr-3" />
                View ASD Roster
              </Button>
            </Link>
            <Link to={createPageUrl("ManageConsequences")}>
              <Button className="w-full justify-start bg-orange-600 hover:bg-orange-700" size="lg">
                <AlertTriangle className="w-5 h-5 mr-3" />
                View EA Consequences
              </Button>
            </Link>
            <Link to={createPageUrl("DrivelineCheck")}>
              <Button className="w-full justify-start" variant="outline" size="lg">
                <Car className="w-5 h-5 mr-3" />
                Driveline EA Check
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
